import {
  AccountName,
  ApiGeneralDataResponse,
  ApiResponse,
  CoinName,
  ConfigurationItem,
  DisplayCurrenciesToNumber,
  ExchangeName,
  GraphData,
  Limits,
  LongShort,
  MarginOptions,
  MarginTypes,
  PrecisionConfig,
  RawChartData,
  RawLineChartDataPoint,
  TimeRange,
  UnixTimestamp,
} from "@shared/types";

type DisplayCurrenciesToNumberWithRatio = DisplayCurrenciesToNumber & {
  ratio: number;
};
export type RawCoinExchangesData = {
  total: DisplayCurrenciesToNumberWithRatio;
  available: DisplayCurrenciesToNumber;
  id: number;
  uuid: string;
  creation_date: number;
  price: DisplayCurrenciesToNumber;
  graph_data: GraphData;
};

export type ProcessedCoinExchangesData = {
  client: string;
  exchange: string;
} & RawCoinExchangesData;

export type CoinExtraExchangesData = {
  [key in ExchangeName]: {
    [key in AccountName]: RawCoinExchangesData;
  };
};

// Responses
export type GetCoinExtraExchangesDataResponse =
  ApiGeneralDataResponse<CoinExtraExchangesData> & {
    exchange_dependency?: ExchangeDependency;
    client_dependency_new?: ClientDependency;
  };

export interface BasePortfolioItem {
  total_in_btc: number;
  total_in_usd: number;
  available_in_btc: number;
  available_in_usd: number;
  graph_data?: RawChartData;
  total_ratio: number;
}

export type ConsolidatedExchangeData = BasePortfolioItem & { exchange: string };
export type ConsolidatedAccountData = ConsolidatedExchangeData & {
  client: string;
  client_uuid: string;
  client_id: number;
  is_multi_asset?: boolean;
};

export interface CoinData extends BasePortfolioItem {
  symbol: string;
  btc_price: number;
  usd_price: number;
  quantity: number;
  available_quantity: number;
  is_sellable: boolean;
  exchange_ratio: number;
  client_ratio: number;
  is_short?: boolean;
  leverage: number;
  margin_type: MarginTypes;
  position_size?: number;
  realized_pnl?: number;
  unrealized_pnl?: number;
  entry_price?: number;
  liquidation_price?: number;
  margin?: [mainCoin: number, BTC: number, USD: number];
  price?: number;
}

export interface PositionData extends CoinData {
  account_id: number;
  exchange: string;
}

export interface ProcessedCoinData extends CoinData, ConsolidatedAccountData {}

export type ConsolidatedCoinData = Omit<
  ProcessedCoinData,
  "client" | "exchange"
> & {
  clients: string[];
  client_ids: number[];
  exchanges: string[];
  accountsSellData: {
    exchange: string;
    account: string;
    sellable: boolean;
    accountId: number;
  }[];
};

export type ConsolidatedCoinDataWithKey = ConsolidatedCoinData & {
  key: string | number;
};

export type ConsolidatedAccountDataWithId = ConsolidatedAccountData & {
  id: number;
};

interface BasePortfolioData {
  total: DisplayCurrenciesToNumber & {
    graph_data?: RawChartData;
    ratio?: number;
  };
  available: DisplayCurrenciesToNumber;
}

export type AccountPortfolioData = BasePortfolioData & {
  uuid: string;
  id: number;
  creation_date: UnixTimestamp;
  coin_cards: (string | number | RawChartData)[][];
  is_multi_asset?: boolean;
};

export type PortfolioData = BasePortfolioData & {
  [key in ExchangeName]: BasePortfolioData & {
    [key in AccountName]: AccountPortfolioData;
  };
};

export type AccountAmountHistoryData = {
  [key in TimeRange]: RawLineChartDataPoint[];
} & {
  uuid: string;
  id: number;
  creation_date: number;
};

export type AmountsHistoryData = {
  [key in ExchangeName]: {
    [key in AccountName]: AccountAmountHistoryData;
  };
};

export enum OrderType {
  ENTRY = "entry",
  TAKE_PROFIT = "take_profit",
  MARKET = "market",
  STOP_LIMIT = "stop_limit",
  STOP_MARKET = "stop_market",
  LIMIT = "limit",
}

export interface CreateOrderData {
  account_id: number;
  symbol: string;
  entry_or_tp: "entry" | "take_profit";
  order_type: OrderType;
  multiplier: number;
  long_short: LongShort;
  cost: number;
  margin_type: MarginTypes;
  limit_price?: number;
  stop_price?: number;
}

type RawCoinGeneralData = [number];

export type CoinExtraGeneralData = {
  [key in ExchangeName]: {
    [key in CoinName]: RawCoinGeneralData;
  };
};

export interface LeverageOptions {
  options: {
    "Margin Type"?: MarginOptions;
    Multiplier?: MarginOptions;
  };
}

export interface RiskLimitOption {
  id: number;
  limit: number;
  max_leverage: number;
}

export type ExchangeDependency = { [key: string]: string };
export type ClientDependency = { [key: number]: number[] };

// Responses
export type GetPortfolioDataResponse = ApiGeneralDataResponse<PortfolioData> & {
  exchange_dependency?: ExchangeDependency;
  client_dependency_new?: ClientDependency;
};
export type GetCoinExtraGeneralDataResponse =
  ApiGeneralDataResponse<CoinExtraGeneralData>;
export type GetAmountsHistoryResponse =
  ApiGeneralDataResponse<AmountsHistoryData> & {
    client_dependency_new?: ClientDependency;
  };

// TODO: Fix these two types!
export type GetAccountExtraInfoGeneralResponse = ApiGeneralDataResponse<any>;
export type GetRiskLimitOptionsResponse = ApiResponse<
  ApiGeneralDataResponse<{ risk_limits: RiskLimitOption[] }>
>;
export type GetRiskLimitResponse = ApiResponse<
  ApiGeneralDataResponse<{ risk_id: number }>
>;

// Requests
export interface GetCoinExtraGeneralDataRequest {
  coin: string;
  time_range: TimeRange;
}

export interface GetPositionExtraGeneralDataRequest {
  symbol: string;
  margin_type?: MarginTypes;
  reverse?: boolean;
  account_id: number;
}

export interface GetCoinExtraExchangesDataRequest {
  coin: string;
}

export interface GetAccountExtraInfoGeneralRequest {
  account_id: number;
  time_range: TimeRange;
}

export interface GetPortfolioByAccountRequest {
  account_id: number;
}

export interface SetLeverageRequest {
  account_id: number;
  symbol: string;
  reverse: boolean;
  margin_type: MarginTypes | null;
  multiplier: number | null;
}

export interface SetRiskLimitRequest {
  account_id: number;
  symbol: string;
  reverse: boolean;
  risk_id: number;
}

export interface GetRiskLimitOptionsRequest {
  account_id: number;
  symbol: string;
}

export interface GetRiskLimitRequest {
  account_id: number;
  symbol: string;
  reverse: boolean;
}

export interface GetPairsRequest {
  account_id: number;
  asset: string;
}

export enum CreateOrderEntryOrTakeProfit {
  ENTRY = "entry",
  TAKE_PROFIT = "take_profit",
}

export interface GetCreateOrderGeneralInfoRequest {
  entry_or_tp: CreateOrderEntryOrTakeProfit;
  account_id: number;
  symbol: string;
  reverse: boolean;
}

export interface CreateOrderGeneralInfo {
  data: {
    current_price: number;
    coin: string;
    stop_market: boolean;
  };
  limits: Limits;
  amount_conversions: string;
  precision: PrecisionConfig;
  configuration: ConfigurationItem;
  long_short_conversion: Record<"long" | "short", LongShort>;
}

export enum PortfolioTab {
  ACCOUNTS = "accounts",
  ASSETS = "assets",
  POSITIONS = "positions",
}
